/**
 * ProgramOverviewSection.js – Support at Home "Program Overview" tab content.
 * Explains Support at Home, 8 classifications, contribution rates, and key features.
 * Used by SupportAtHomePage.js.
 */
import React, { useState } from 'react';
import { ChevronDown, Home, Calculator, DollarSign, Info, ShieldCheck, Clock, TrendingUp, FileText, Landmark, Building2, HeartPulse, Activity } from 'lucide-react';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import { IconTile, InsightCard } from '../../components/ui/index.tsx';

type ExpandedSection = 'classifications' | 'contributions' | null;

/*
 * THE FORK INSTEAD OF THE DEAD END (owner-approved, 2026-08-28).
 *
 * A client who has not been assessed reaches this module and can go no further: Support at Home
 * costs depend on a classification they do not have. Until now the page simply stopped there, and
 * since the client has already PAID by this point, stopping reads as "I got nothing for my money".
 *
 * It is also the wrong conclusion. Someone on entry-level support is at the START of the journey,
 * not the end — they still have the home, the pension and every decision ahead of them, so they
 * have MORE to gain from the rest of the app than someone already in residential care, who has the
 * fewest moves left. This panel says so and hands them the door.
 *
 * Deliberately only on this tab. Interrupting someone who has chosen to explore a what-if on
 * Contributions or Service Builder with "here is other stuff" is nagging, not helping.
 */
/*
 * THE PAGE KEYS MUST MATCH App.tsx's SWITCH, which falls through to a "coming soon" placeholder for
 * anything it does not recognise. The first cut used 'AgedCareAnalyser' and 'DownsizerPage' —
 * neither exists — so three of the four tiles led to a dead placeholder page (owner-caught on the
 * built screen, 2026-08-28). The live cases are: Dashboard, Client Profile, AgedCarePage,
 * AssumptionsPage, HomeCarePage, RetirementVillagePage, GrannyFlatPage, DownsizingPage.
 *
 * The Age Pension tile points at the DASHBOARD deliberately: that is the only screen holding a
 * calculated Age Pension figure with its means-test breakdown. It is the thinnest of the four, and
 * the gap a dedicated cashflow/pension view would fill.
 */
const WHILE_YOU_WAIT: Array<{ page: string; icon: typeof Home; title: string; blurb: string }> = [
    {
        page: 'CashflowPensionPage',
        icon: Landmark,
        title: 'Check your cashflow and Age Pension',
        blurb: 'Where you stand now, how each figure is worked out, and what support at home costs you.',
    },
    {
        page: 'DownsizingPage',
        icon: Home,
        title: 'Model a downsize',
        blurb: 'Including the pension trap — and when buying dearer beats buying cheaper.',
    },
    {
        page: 'RetirementVillagePage',
        icon: Building2,
        title: 'Retirement village or granny flat',
        blurb: 'What the entry contribution does to your pension and your estate.',
    },
    {
        page: 'AgedCarePage',
        icon: HeartPulse,
        title: 'Plan ahead for residential care',
        blurb: 'Room costs and the means-tested fee, before you’re deciding under pressure.',
    },
];

const WhileYouWaitPanel = ({ onNavigate }: { onNavigate: (page: string) => void }) => (
    <InsightCard tone="info" heading="While you wait for an assessment, here’s what we can do today" icon={Activity}>
        <p className="text-sm text-gray-700 mb-3">
            None of these depend on a care assessment — and each one is worth more the earlier you look at it.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {WHILE_YOU_WAIT.map((item) => (
                <button
                    key={item.title}
                    type="button"
                    onClick={() => onNavigate(item.page)}
                    className="flex items-start gap-3 rounded-xl border border-slate-200/90 bg-white p-3.5 text-left transition-shadow hover:shadow-md"
                >
                    <IconTile icon={item.icon} tone="brand" variant="solid" size="sm" />
                    <span>
                        <span className="block text-sm font-bold text-gray-800">{item.title}</span>
                        <span className="block text-xs text-slate-500 leading-relaxed">{item.blurb}</span>
                    </span>
                </button>
            ))}
        </div>
        <p className="text-[13px] text-slate-500 mt-3.5">
            We’ll pick Support at Home back up the moment you have an assessment — nothing you enter now is lost.
        </p>
    </InsightCard>
);

export interface ProgramOverviewSectionProps {
    /** Manually-selected classification annual budget ($), unused by this read-only overview but accepted for prop-shape parity with sibling sections. */
    selectedLevel?: number | null;
    clientLevel?: { budget?: number; [key: string]: unknown } | null;
    onNext?: () => void;
    /** True when nobody on the file has an assessed classification — see WHILE_YOU_WAIT above. */
    awaitingAssessment?: boolean;
    /** Sends the client to another module. Absent → the panel is not offered. */
    onNavigate?: (page: string) => void;
}

const ProgramOverviewSection: React.FC<ProgramOverviewSectionProps> = ({ awaitingAssessment = false, onNavigate }) => {
    const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    };

    return (
        <div className="space-y-6">
            {/* What is Support at Home? */}
            <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6">
                <div className="flex items-start gap-4">
                    <IconTile icon={Home} tone="brand" variant="solid" size="lg" />
                    <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">What is Support at Home?</h4>
                        <p className="text-gray-700 leading-relaxed">
                            Support at Home is the Australian Government's new aged care program that provides flexible, 
                            personalised support to help you continue living in your own home. Replacing Home Care Packages 
                            from 1 November 2025, it offers 8 funding classifications to match your care needs, from 
                            basic support (Level 1) to high care requirements (Level 8).
                        </p>
                    </div>
                </div>
            </div>

            {/* 8 Classifications Explained */}
            <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 overflow-hidden">
                <button
                    onClick={() => setExpandedSection(expandedSection === 'classifications' ? null : 'classifications')}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-blue-50 transition-colors"
                >
                    <div className="flex items-center gap-4">
                        <IconTile icon={Calculator} tone="brand" variant="soft" size="md" />
                        <h3 className="text-xl font-bold text-gray-800">8 Classifications Explained</h3>
                    </div>
                    <ChevronDown className={`w-6 h-6 text-gray-500 transition-transform duration-300 ${expandedSection === 'classifications' ? 'rotate-180' : ''}`} />
                </button>
                {expandedSection === 'classifications' && (
                    <div className="px-6 pb-6 pl-22">
                        <div className="p-4 bg-white border-l-4 border-blue-400 rounded-r-lg">
                            <p className="text-gray-700 mb-4">
                                Your classification determines your annual budget. Higher classifications provide more funding 
                                for additional services and support.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(ACAT_LEVEL_DATA).filter(([, value]) => !value.transitional).map(([key, value]) => (
                                    <div key={key} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors bg-white hover:shadow-md">
                                        <div className="flex items-start justify-between mb-2">
                                            <span className="font-semibold text-gray-900 text-sm">{value.label}</span>
                                            <span className="font-bold text-blue-600 ml-2">{formatCurrency(value.budget)}</span>
                                        </div>
                                        <p className="text-xs text-gray-600 leading-relaxed mb-2">
                                            {value.description}
                                        </p>
                                        <div className="text-xs text-gray-500 font-medium">
                                            {Math.round(value.budget / 4).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 })} per quarter
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* How Contributions Work */}
            <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 overflow-hidden">
                <button
                    onClick={() => setExpandedSection(expandedSection === 'contributions' ? null : 'contributions')}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-blue-50 transition-colors"
                >
                    <div className="flex items-center gap-4">
                        <IconTile icon={DollarSign} tone="brand" variant="soft" size="md" />
                        <h3 className="text-xl font-bold text-gray-800">How Participant Contributions Work</h3>
                    </div>
                    <ChevronDown className={`w-6 h-6 text-gray-500 transition-transform duration-300 ${expandedSection === 'contributions' ? 'rotate-180' : ''}`} />
                </button>
                {expandedSection === 'contributions' && (
                    <div className="px-6 pb-6 pl-22">
                        <div className="p-4 bg-white border-l-4 border-blue-400 rounded-r-lg space-y-4">
                            <p className="text-gray-700">
                                From 1 November 2025, participants contribute towards the cost of some services based on their 
                                income and assets. The government covers the remainder.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="border-2 border-blue-300 bg-white rounded-lg p-4">
                                    <h5 className="font-bold text-gray-900 mb-2">Clinical Care</h5>
                                    <p className="text-sm text-gray-600 mb-3">Nursing, physiotherapy, personal care (fully funded from 1 Oct 2026)</p>
                                    <div className="text-2xl font-bold text-gray-900">0%</div>
                                    <div className="text-xs text-gray-600 mt-1">Government funded</div>
                                </div>
                                <div className="border-2 border-green-300 bg-white rounded-lg p-4">
                                    <h5 className="font-bold text-gray-900 mb-2">Independence</h5>
                                    <p className="text-sm text-gray-600 mb-3">Social support, transport, respite</p>
                                    <div className="text-2xl font-bold text-gray-900">Varies</div>
                                    <div className="text-xs text-gray-600 mt-1">5-50% based on pension</div>
                                </div>
                                <div className="border-2 border-teal-300 bg-white rounded-lg p-4">
                                    <h5 className="font-bold text-gray-900 mb-2">Everyday Living</h5>
                                    <p className="text-sm text-gray-600 mb-3">Cleaning, gardening, meals</p>
                                    <div className="text-2xl font-bold text-gray-900">Varies</div>
                                    <div className="text-xs text-gray-600 mt-1">17.5-80% based on pension</div>
                                </div>
                            </div>

                            <div className="bg-white border border-blue-200 rounded-lg p-4 mt-4">
                                <h5 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                                    <Info size={18} /> Contribution Rates by Pension Status
                                </h5>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-blue-200">
                                                <th className="text-left py-2 px-3 font-semibold text-blue-900">Pension Status</th>
                                                <th className="text-center py-2 px-3 font-semibold text-blue-900">Clinical</th>
                                                <th className="text-center py-2 px-3 font-semibold text-blue-900">Independence</th>
                                                <th className="text-center py-2 px-3 font-semibold text-blue-900">Everyday</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b border-blue-100">
                                                <td className="py-2 px-3">Full Pensioner</td>
                                                <td className="text-center py-2 px-3">0%</td>
                                                <td className="text-center py-2 px-3">5%</td>
                                                <td className="text-center py-2 px-3">17.5%</td>
                                            </tr>
                                            <tr className="border-b border-blue-100">
                                                <td className="py-2 px-3">Part Pensioner/CSHC</td>
                                                <td className="text-center py-2 px-3">0%</td>
                                                <td className="text-center py-2 px-3">5-50%</td>
                                                <td className="text-center py-2 px-3">17.5-80%</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 px-3">Self-Funded Retiree</td>
                                                <td className="text-center py-2 px-3">0%</td>
                                                <td className="text-center py-2 px-3">50%</td>
                                                <td className="text-center py-2 px-3">80%</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Key Features Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-start gap-3">
                        <IconTile icon={ShieldCheck} tone="brand" variant="solid" size="md" />
                        <div>
                            <h5 className="font-bold text-gray-900 mb-2">Lifetime Cap</h5>
                            <p className="text-sm text-gray-700">
                                Once you've contributed $130,000 towards your care, you'll never pay fees again.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-start gap-3">
                        <IconTile icon={Clock} tone="brand" variant="solid" size="md" />
                        <div>
                            <h5 className="font-bold text-gray-900 mb-2">Quarterly Budgets</h5>
                            <p className="text-sm text-gray-700">
                                Your budget is split into 4 equal quarters and managed by Services Australia.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-start gap-3">
                        <IconTile icon={TrendingUp} tone="brand" variant="solid" size="md" />
                        <div>
                            <h5 className="font-bold text-gray-900 mb-2">Accrual Benefits</h5>
                            <p className="text-sm text-gray-700">
                                Save up to $1,000 or 10% of your quarterly budget (whichever is higher) for unplanned needs.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-start gap-3">
                        <IconTile icon={FileText} tone="brand" variant="solid" size="md" />
                        <div>
                            <h5 className="font-bold text-gray-900 mb-2">Monthly Statements</h5>
                            <p className="text-sm text-gray-700">
                                Your provider will issue itemised monthly statements showing budget usage.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {awaitingAssessment && onNavigate && <WhileYouWaitPanel onNavigate={onNavigate} />}
        </div>
    );
};

export default ProgramOverviewSection;
